import React, {
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react'
import {
    AccessTime,
    Circle,
    Computer,
    DirectionsBoat,
    InfoOutlined,
    Public,
} from '@mui/icons-material'
import {
    Box,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import { getManufacturers } from '../../../../../../apiRoutes/manufacturers'
import dayjs, { Dayjs } from 'dayjs'
import {
    Grid,
    UniversalGoogleMapV2,
    UniversalTimeRange,
} from '../../../../../UniversalComponents'
import { FleetStatusBar, STATUS_COLORS } from '../../FleetStatusBar'
import {
    DealerPerformanceTable,
    DealerPerf,
} from '../../DealerPerformanceTable'
import { DrillTable, DrillNode } from '../../DrillTable'
import {
    TopPerformersCard,
    CountOption,
} from '../../TopPerformersCard'
import { Sparkline } from '../../Sparkline'
import {
    ServiceTable,
    ServiceRow,
    BoatDetailData,
} from '../../ServiceTable'
import { useSocket } from '../../../../../../context/SocketContext'
import { useAuth } from '../../../../../../context/AuthenticationContext'
import { useGlobalState } from '../../../../../../context/GlobalStateContext'

// Individual boat point from the backend (compact keys to keep the fleet
// payload small): la=lat, ln=lng, s=status label, id=serial.
interface BoatPoint {
    la: number
    ln: number
    s: string
    id: string
}

interface KpiMetric {
    value: number
    deltaPct: number | null
    series: number[]
    invert?: boolean
}

interface Kpis {
    rangeDays: number
    degraded: boolean
    // ISO date (YYYY-MM-DD) per daily sparkline point, aligned across series.
    days?: string[]
    registered: KpiMetric
    connected: KpiMetric
    active: KpiMetric
    inactive: KpiMetric
    totalHours: KpiMetric
    hrsPerActive: KpiMetric
    dealerPerformance?: DealerPerf[]
    boatPerformance?: DealerPerf[]
}

const RANGE_OPTIONS = [7, 14, 30, 90]

const KPI_DEFS: {
    key: keyof Omit<Kpis, 'rangeDays' | 'degraded' | 'dealerPerformance'>
    label: string
    color: string
    fmt: (v: number) => string
    description: string
}[] = [
    {
        key: 'registered',
        label: 'Registered',
        color: '#6E9BD1',
        fmt: (v) => v.toLocaleString(),
        description:
            'All boats registered to this manufacturer in Osmosis, whether or not they have a connected telemetry device.',
    },
    {
        key: 'connected',
        label: 'Connected',
        color: '#508991',
        fmt: (v) => v.toLocaleString(),
        description:
            'Boats that checked in (reported telemetry) at least once during the selected time period.',
    },
    {
        key: 'active',
        label: 'Active',
        color: '#8BC441',
        fmt: (v) => v.toLocaleString(),
        description:
            'Boats that accumulated engine hours during the selected time period.',
    },
    {
        key: 'inactive',
        label: 'Inactive',
        color: '#E0727A',
        fmt: (v) => v.toLocaleString(),
        description:
            'Boats that did not accumulate any engine hours during the selected time period.',
    },
    {
        key: 'totalHours',
        label: 'Total Engine Hours',
        color: '#6E9BD1',
        fmt: (v) => v.toLocaleString(),
        description:
            'Total engine hours accumulated across the fleet during the selected time period.',
    },
    {
        key: 'hrsPerActive',
        label: 'Hrs / Active Boat',
        color: '#AA3E98',
        fmt: (v) => v.toFixed(1),
        description:
            'Average engine hours per active boat over the selected period (Total Engine Hours ÷ Active boats).',
    },
]

// Isolated clock — owns its own interval so the tick doesn't re-render the page.
// Shows three 24-hour clocks: UTC, the viewer's local time, and US Eastern
// (auto EST/EDT). en-GB locale gives a clean HH:MM:SS, 24-hour format.
const hms = (tz?: string) =>
    new Date().toLocaleTimeString('en-GB', {
        hour12: false,
        ...(tz ? { timeZone: tz } : {}),
    })

// Viewer's local timezone abbreviation (e.g. EDT, PST, GMT+2).
const localTzLabel = (() => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZoneName: 'short',
        }).formatToParts(new Date())
        return parts.find((p) => p.type === 'timeZoneName')?.value || 'LOCAL'
    } catch {
        return 'LOCAL'
    }
})()

const FleetClock: React.FC<{ highlightId?: string | null }> = React.memo(
    function FleetClock({ highlightId }) {
    const [times, setTimes] = useState({ utc: '', local: '', et: '' })
    useEffect(() => {
        const update = () =>
            setTimes({
                utc: hms('UTC'),
                local: hms(),
                et: hms('America/New_York'),
            })
        update()
        const id = setInterval(update, 1000)
        return () => clearInterval(id)
    }, [])
    // Stable ids keep React keys unique even when two zones share a label
    // (e.g. a viewer in UTC or US Eastern, where localTzLabel === 'UTC'/'ET').
    // In that case the redundant local clock is dropped rather than shown twice.
    const rows: [string, string, string][] = [
        ['utc', 'UTC', times.utc],
        ['local', localTzLabel, times.local],
        ['et', 'ET', times.et],
    ].filter(
        ([id, label]) =>
            id !== 'local' || (label !== 'UTC' && label !== 'ET')
    ) as [string, string, string][]
    return (
        <Box sx={{ display: 'grid', rowGap: 0.1 }}>
            {rows.map(([id, label, value]) => {
                // The row matching the active tz mode is highlighted so the
                // selected setting is easy to read off at a glance.
                const active = highlightId === id
                return (
                    <Box
                        key={id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 0.75,
                            borderRadius: 0.5,
                            fontFamily: 'monospace',
                            bgcolor: active
                                ? 'rgba(139,196,65,0.14)'
                                : 'transparent',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: active ? 'primary.main' : 'text.disabled',
                                fontFamily: 'inherit',
                                fontWeight: active ? 700 : 400,
                                minWidth: 38,
                            }}
                        >
                            {label}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: active
                                    ? 'primary.main'
                                    : 'text.secondary',
                                fontFamily: 'inherit',
                                fontWeight: active ? 700 : 400,
                            }}
                        >
                            {value}
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
})


const DeltaChip: React.FC<{ pct: number | null; invert?: boolean }> = ({
    pct,
    invert,
}) => {
    if (pct == null || pct === 0) return null
    const up = pct > 0
    const good = invert ? !up : up
    return (
        <Box
            component="span"
            sx={{
                ml: 1,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                color: good ? '#8BC441' : '#E00000',
            }}
        >
            {up ? '▲' : '▼'} {Math.abs(pct)}%
        </Box>
    )
}

const KpiStatCard: React.FC<{
    label: string
    value: string
    pct: number | null
    invert?: boolean
    series: number[]
    color: string
    info?: string
    fmt?: (v: number) => string
    days?: string[]
}> = React.memo(function KpiStatCard({
    label,
    value,
    pct,
    invert,
    series,
    color,
    info,
    fmt,
    days,
}) {
    // Sparkline tracks the delta's sentiment (matches the chip): an improving
    // trend is green, a worsening one red. `invert` flips it for metrics where
    // down is good (e.g. Inactive). Flat / no-prior-window falls back to the
    // card's accent colour.
    const trendColor =
        pct == null || pct === 0
            ? color
            : (invert ? pct < 0 : pct > 0)
            ? '#8BC441'
            : '#E00000'
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                flex: '1 1 200px',
                minWidth: 180,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 0.5,
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        color: 'text.secondary',
                        display: 'block',
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </Typography>
                {info && (
                    <Tooltip title={info} arrow placement="top">
                        <InfoOutlined
                            sx={{
                                fontSize: 15,
                                color: 'text.disabled',
                                cursor: 'help',
                                flexShrink: 0,
                                mt: '2px',
                                '&:hover': { color: 'text.secondary' },
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {value}
                </Typography>
                <DeltaChip pct={pct} invert={invert} />
            </Box>
            <Box sx={{ mt: 1 }}>
                <Sparkline
                    data={series}
                    color={trendColor}
                    fmt={fmt}
                    days={days}
                />
            </Box>
        </Paper>
    )
})

const PanelHeader: React.FC<{ title: string; caption?: string }> = ({
    title,
    caption,
}) => (
    <Box
        sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
        }}
    >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
        </Typography>
        {caption && (
            <Tooltip title={caption} arrow placement="top">
                <InfoOutlined
                    sx={{
                        fontSize: 15,
                        color: 'text.disabled',
                        cursor: 'help',
                        '&:hover': { color: 'text.secondary' },
                    }}
                />
            </Tooltip>
        )}
    </Box>
)

const FleetHomeView: React.FC = () => {
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser
    const { auth } = useAuth()
    const { user } = auth
    const { apiSocket } = useSocket()

    // Dealer context: a dealer's fleet is scoped to the dealers assigned to
    // their dealer profile (Boat.dealer ∈ profile.dealers), NOT a manufacturer.
    const assignedDealerProfile: any = (oupUser as any)?.assignedDealerProfile
    const isDealer = (oupUser as any)?.userProfile?.dealerProfile === true
    const dealerIds: string[] = isDealer
        ? (assignedDealerProfile?.dealers || []).map((d: any) =>
              String(d?._id || d)
          )
        : []
    const dealerIdsKey = dealerIds.join(',')
    const dealerScopeLabel =
        assignedDealerProfile?.dealerName ||
        assignedDealerProfile?.dealerProfileName ||
        'Dealer'
    const dealerMfrIds: string[] = isDealer
        ? (assignedDealerProfile?.manufacturers || []).map((m: any) =>
              String(m?._id || m)
          )
        : []
    const dealerMfrKey = dealerMfrIds.join(',')

    // Manufacturer picker (dealer only): '' = all of the dealer's manufacturers.
    const [selectedMfr, setSelectedMfr] = useState('')
    const [mfrOptions, setMfrOptions] = useState<
        { _id: string; name: string }[]
    >([])

    // Whole-fleet boat search (Boats tab): queries the backend for any boat by
    // HIN/model within scope, replacing the top-10 list with the matches.
    const [boatSearch, setBoatSearch] = useState('')
    const [searchResults, setSearchResults] = useState<DealerPerf[] | null>(
        null
    )
    const [searchDays, setSearchDays] = useState<string[]>([])
    const [searching, setSearching] = useState(false)
    const searchActive = boatSearch.trim().length >= 2
    // Universal row-count selector (Top 10 / 20 / All), shared by every tab.
    const [count, setCount] = useState<CountOption>(10)

    // Service tab: boats with alarm/DTC counts + lazily-loaded per-boat events.
    const [serviceRows, setServiceRows] = useState<ServiceRow[]>([])
    const [serviceLoading, setServiceLoading] = useState(false)
    const [detailBySerial, setDetailBySerial] = useState<
        Record<string, BoatDetailData | undefined>
    >({})
    const [loadingEventSerial, setLoadingEventSerial] = useState<string | null>(
        null
    )

    const [rangeDays, setRangeDays] = useState(14)
    // Custom date range (calendar picker). When both set, it overrides rangeDays.
    const [customRange, setCustomRange] = useState<{
        from: Dayjs | null
        to: Dayjs | null
    }>({ from: null, to: null })
    // Timezone the fault event timestamps render in. 'boat' is resolved per-boat
    // from its GPS (downstream), so we pass the mode rather than a fixed zone.
    // Persisted so other surfaces (e.g. the notification cards) honor the same
    // choice.
    const [tzMode, setTzMode] = useState<'browser' | 'boat' | 'utc'>(
        () =>
            ((typeof localStorage !== 'undefined' &&
                localStorage.getItem('osmosisFleetTz')) as
                | 'browser'
                | 'boat'
                | 'utc') || 'browser'
    )
    useEffect(() => {
        try {
            localStorage.setItem('osmosisFleetTz', tzMode)
        } catch {
            /* ignore storage failures */
        }
    }, [tzMode])
    // Statuses hidden from the map (drives marker visibility). Default: ALL
    // statuses visible — the user can hide individual ones by clicking the bar
    // or legend.
    const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(
        new Set()
    )
    // Top Performers tab: dealers (default) vs location (country).
    const [perfTab, setPerfTab] = useState<
        'dealers' | 'location' | 'boats' | 'service'
    >('dealers')
    // Dealers see a "Boats" tab instead of "Dealers" — default them to it
    // (without overriding a manual switch to Location).
    useEffect(() => {
        if (isDealer) setPerfTab((p) => (p === 'dealers' ? 'boats' : p))
    }, [isDealer])

    // Clicking a boat row focuses it on the fleet map (pan + zoom + scroll).
    const [focusPoint, setFocusPoint] = useState<{
        lat: number
        lng: number
        id?: string
    } | null>(null)
    // The boat row currently focused on the map (Boats tab) — click again to
    // un-focus (re-cluster + zoom out).
    const [focusedBoatId, setFocusedBoatId] = useState<string | null>(null)
    // Bumped to snap the map back to its default view (e.g. on row collapse).
    const [resetView, setResetView] = useState(0)
    const mapRef = useRef<HTMLDivElement>(null)

    const customActive = Boolean(customRange.from && customRange.to)

    const toggleStatus = (s: string) =>
        setHiddenStatuses((prev) => {
            const next = new Set(prev)
            next.has(s) ? next.delete(s) : next.add(s)
            return next
        })

    const defaultState = {
        loading: true,
        telemetryReady: false,
        kpis: null as Kpis | null,
        byModel: [] as { model: string; count: number }[],
        byStatus: [] as { boatStatus: string; count: number }[],
        topDealers: [] as { dealer: string; count: number }[],
        points: [] as BoatPoint[],
        locationPerformance: [] as DealerPerf[],
        locationHierarchy: [] as DrillNode[],
        dealerHierarchy: [] as DrillNode[],
        boatPerformanceAll: [] as DealerPerf[],
    }

    const reducer = (state: any, newState: any) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const {
        loading,
        telemetryReady,
        kpis,
        byStatus,
        points,
        locationHierarchy,
        dealerHierarchy,
        boatPerformanceAll,
    } = state

    useEffect(() => {
        if (!apiSocket || !oupUser) return
        // Dealers scope by their dealer profile, so they don't need an active
        // manufacturer; everyone else still does.
        if (!isDealer && !activeManufacturer) return
        // Wait for the dealer's assigned dealers to load — emitting with an
        // empty dealer list would scope to nothing (REGISTERED 0).
        if (isDealer && dealerIds.length === 0) return

        setState({ loading: true, telemetryReady: false, kpis: null })

        const handler = (response: any) => {
            if (!response || response.error) {
                setState({ loading: false })
                return
            }
            const next: any = { loading: false }
            if (response.phase === 'kpis') {
                next.kpis = response.kpis ?? null
            } else {
                if ('byModel' in response) next.byModel = response.byModel || []
                if ('byStatus' in response)
                    next.byStatus = response.byStatus || []
                if ('topDealers' in response)
                    next.topDealers = response.topDealers || []
                if ('points' in response) next.points = response.points || []
                if ('locationPerformance' in response)
                    next.locationPerformance =
                        response.locationPerformance || []
                if ('locationHierarchy' in response)
                    next.locationHierarchy =
                        response.locationHierarchy || []
                if ('dealerHierarchy' in response)
                    next.dealerHierarchy = response.dealerHierarchy || []
                if ('boatPerformanceAll' in response)
                    next.boatPerformanceAll =
                        response.boatPerformanceAll || []
                if (response.telemetryReady) next.telemetryReady = true
            }
            setState(next)
        }

        apiSocket.emit('allFleetLiveData', {
            oupUser,
            user,
            // Dealer → scope by the profile's dealers + optional manufacturer
            // filter. Otherwise scope by the active manufacturer.
            ...(isDealer
                ? {
                      dealerIds,
                      manufacturerId: selectedMfr || undefined,
                  }
                : { manufacturerId: activeManufacturer._id }),
            ...(customRange.from && customRange.to
                ? {
                      fromMs: customRange.from.startOf('day').valueOf(),
                      toMs: customRange.to.endOf('day').valueOf(),
                  }
                : { rangeDays }),
        })
        apiSocket.on('allFleetLiveDataRes', handler)

        return () => {
            apiSocket.off('allFleetLiveDataRes', handler)
        }
        // Depend on the manufacturer ID + window only — NOT the oupUser/user
        // objects, whose reference changes on unrelated GlobalState re-renders
        // and was causing repeated full fleet re-fetches (CH query storms).
    }, [
        apiSocket,
        activeManufacturer?._id,
        isDealer,
        dealerIdsKey,
        selectedMfr,
        rangeDays,
        customRange.from,
        customRange.to,
    ])

    // Resolve the dealer's manufacturer-picker options (names) once we know the
    // dealer's profile manufacturers.
    useEffect(() => {
        if (!isDealer || dealerMfrIds.length === 0) {
            setMfrOptions([])
            return
        }
        let cancelled = false
        getManufacturers(true).then((all: any) => {
            if (cancelled || !Array.isArray(all)) return
            setMfrOptions(
                all
                    .filter((m: any) => dealerMfrIds.includes(String(m?._id)))
                    .map((m: any) => ({
                        _id: String(m._id),
                        name: m.manufacturerName,
                    }))
            )
        })
        return () => {
            cancelled = true
        }
    }, [isDealer, dealerMfrKey])

    // Whole-fleet boat search: debounce the term, emit to the backend with the
    // same scope/window as the fleet view, and stream the matches back.
    useEffect(() => {
        if (!apiSocket) return
        const term = boatSearch.trim()
        // Only the Boats tab uses the server-side fleet search; on other tabs the
        // same box is a client-side filter handled in each grid.
        if (perfTab !== 'boats' || term.length < 2) {
            setSearchResults(null)
            setSearching(false)
            return
        }
        setSearching(true)
        const onRes = (res: any) => {
            setSearching(false)
            setSearchResults(Array.isArray(res?.results) ? res.results : [])
            setSearchDays(Array.isArray(res?.days) ? res.days : [])
        }
        apiSocket.on('fleetBoatSearchRes', onRes)
        const id = setTimeout(() => {
            apiSocket.emit('fleetBoatSearch', {
                oupUser,
                user,
                ...(isDealer
                    ? { dealerIds, manufacturerId: selectedMfr || undefined }
                    : { manufacturerId: activeManufacturer?._id }),
                ...(customRange.from && customRange.to
                    ? {
                          fromMs: customRange.from.startOf('day').valueOf(),
                          toMs: customRange.to.endOf('day').valueOf(),
                      }
                    : { rangeDays }),
                term,
            })
        }, 350)
        return () => {
            clearTimeout(id)
            apiSocket.off('fleetBoatSearchRes', onRes)
        }
    }, [
        boatSearch,
        perfTab,
        apiSocket,
        isDealer,
        dealerIdsKey,
        selectedMfr,
        activeManufacturer?._id,
        rangeDays,
        customRange.from,
        customRange.to,
    ])

    // Shared scope + window params for the service socket calls.
    const windowParams = () =>
        customRange.from && customRange.to
            ? {
                  fromMs: customRange.from.startOf('day').valueOf(),
                  toMs: customRange.to.endOf('day').valueOf(),
              }
            : { rangeDays }
    const scopeParams = () =>
        isDealer
            ? { dealerIds, manufacturerId: selectedMfr || undefined }
            : { manufacturerId: activeManufacturer?._id }

    // Service tab: fetch the alarm/DTC boat list for the current scope/window.
    useEffect(() => {
        if (!apiSocket || perfTab !== 'service') return
        setServiceLoading(true)
        const onRes = (res: any) => {
            setServiceLoading(false)
            setServiceRows(Array.isArray(res?.rows) ? res.rows : [])
        }
        apiSocket.on('fleetServiceListRes', onRes)
        apiSocket.emit('fleetServiceList', {
            oupUser,
            user,
            ...scopeParams(),
            ...windowParams(),
        })
        return () => {
            apiSocket.off('fleetServiceListRes', onRes)
        }
    }, [
        perfTab,
        apiSocket,
        isDealer,
        dealerIdsKey,
        selectedMfr,
        activeManufacturer?._id,
        rangeDays,
        customRange.from,
        customRange.to,
    ])

    // Per-boat event responses (lazy, on expand). Invalidate the cache when the
    // scope/window changes so expansions reflect the current period.
    useEffect(() => {
        setDetailBySerial({})
    }, [rangeDays, customRange.from, customRange.to, selectedMfr, dealerIdsKey])

    useEffect(() => {
        if (!apiSocket) return
        const onEv = (res: any) => {
            setLoadingEventSerial(null)
            setDetailBySerial((prev) => ({
                ...prev,
                [res.serial]: {
                    events: Array.isArray(res?.events) ? res.events : [],
                    hin: res?.hin,
                    model: res?.model,
                    dealer: res?.dealer,
                    hoursByDay: res?.hoursByDay || {},
                },
            }))
        }
        apiSocket.on('fleetBoatEventsRes', onEv)
        return () => {
            apiSocket.off('fleetBoatEventsRes', onEv)
        }
    }, [apiSocket])

    const handleServiceExpand = (serial: string) => {
        if (!apiSocket || detailBySerial[serial]) return
        setLoadingEventSerial(serial)
        apiSocket.emit('fleetBoatEvents', { serial, ...windowParams() })
    }

    // Lazy per-boat detail for the map hover tooltip. We request a boat's detail
    // only when it's hovered (zoomed in to a single pin), cache the result, and
    // resolve a pending promise when the socket responds.
    const boatDetailCache = useRef(new Map<string, any>())
    const boatDetailPending = useRef(
        new Map<string, (d: any) => void>()
    )
    useEffect(() => {
        if (!apiSocket) return
        const onDetail = (d: any) => {
            if (!d?.serial) return
            boatDetailCache.current.set(d.serial, d)
            const resolve = boatDetailPending.current.get(d.serial)
            if (resolve) {
                resolve(d)
                boatDetailPending.current.delete(d.serial)
            }
        }
        apiSocket.on('fleetBoatDetailRes', onDetail)
        return () => {
            apiSocket.off('fleetBoatDetailRes', onDetail)
        }
    }, [apiSocket])

    // Invalidate cached boat detail when the window changes so the tooltip
    // sparkline reflects the selected period.
    useEffect(() => {
        boatDetailCache.current.clear()
    }, [rangeDays, customRange.from, customRange.to])

    const fetchBoatDetail = useMemo(
        () => (serial: string) => {
            const cache = boatDetailCache.current
            if (cache.has(serial)) return Promise.resolve(cache.get(serial))
            return new Promise<any>((resolve) => {
                boatDetailPending.current.set(serial, resolve)
                apiSocket?.emit('fleetBoatDetail', {
                    serial,
                    ...(customRange.from && customRange.to
                        ? {
                              fromMs: customRange.from
                                  .startOf('day')
                                  .valueOf(),
                              toMs: customRange.to.endOf('day').valueOf(),
                          }
                        : { rangeDays }),
                })
                // Safety: resolve null if the backend never answers.
                setTimeout(() => {
                    if (boatDetailPending.current.has(serial)) {
                        boatDetailPending.current.delete(serial)
                        resolve(null)
                    }
                }, 8000)
            })
        },
        [apiSocket, rangeDays, customRange.from, customRange.to]
    )

    const fleetGps = useMemo(() => {
        // Individual boat points, filtered to the currently-visible statuses and
        // colored to match the bar. The map clusters these adaptively by zoom.
        const out: {
            lat: number
            lng: number
            name: string
            status: string
            color: string
            serial: string
        }[] = []
        points.forEach((p: BoatPoint) => {
            if (hiddenStatuses.has(p.s)) return
            out.push({
                lat: p.la,
                lng: p.ln,
                name: p.s,
                status: p.s,
                color: STATUS_COLORS[p.s] || '#9AA0A6',
                serial: p.id,
            })
        })
        return out
    }, [points, hiddenStatuses])

    // Map each boat (by device serial) to its live fleet status, so the Boats
    // tab can show a status beacon matching the map.
    const serialToStatus = useMemo(() => {
        const m = new Map<string, string>()
        points.forEach((p: BoatPoint) => m.set(p.id, p.s))
        return m
    }, [points])

    // The Boats-tab rows (top 10, or search results), enriched with status.
    const boatRows = useMemo(() => {
        const base: DealerPerf[] = searchActive
            ? searchResults || []
            : kpis?.boatPerformance || []
        return base.map((b: any) => ({
            ...b,
            status: serialToStatus.get(b.id) || 'Offline',
        }))
    }, [searchActive, searchResults, kpis?.boatPerformance, serialToStatus])

    // Light "all boats" rows (count = All), enriched with live status.
    const boatRowsAll = useMemo(
        () =>
            (boatPerformanceAll || []).map((b: any) => ({
                ...b,
                status: serialToStatus.get(b.id) || b.status || 'Offline',
            })),
        [boatPerformanceAll, serialToStatus]
    )

    // Boats grid rows: server search results / rich top-N, or the light
    // all-boats list. "All" (without an active search) drops the sparkline for
    // the denser view. Count slices the result.
    const boatsLight = !searchActive && count === 'all'
    const boatsData = useMemo(() => {
        const base = boatsLight ? boatRowsAll : boatRows
        return count === 'all' ? base : base.slice(0, count)
    }, [boatsLight, boatRowsAll, boatRows, count])

    // Service rows: universal text filter (HIN/model/location) + count slice.
    const serviceRowsView = useMemo(() => {
        const term = boatSearch.trim().toLowerCase()
        const filtered = term
            ? serviceRows.filter((x: ServiceRow) =>
                  [x.hin, x.model, x.city, x.state, x.country]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase()
                      .includes(term)
              )
            : serviceRows
        return count === 'all' ? filtered : filtered.slice(0, count)
    }, [serviceRows, boatSearch, count])

    // Click a boat row to focus it on the map (decluster + zoom + tooltip);
    // click the same row again to un-focus (re-cluster + zoom back to default).
    const handleBoatClick = (row: DealerPerf) => {
        if (focusedBoatId === row.id) {
            setFocusedBoatId(null)
            setFocusPoint(null)
            setResetView((v) => v + 1)
            return
        }
        const p = points.find((pt: BoatPoint) => pt.id === row.id)
        if (p) {
            if (hiddenStatuses.has(p.s)) {
                setHiddenStatuses((prev) => {
                    const next = new Set(prev)
                    next.delete(p.s)
                    return next
                })
            }
            setFocusPoint({ lat: p.la, lng: p.ln, id: row.id })
            setFocusedBoatId(row.id || null)
        }
        mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const rangeLabel = customActive
        ? `${customRange.from?.format('MMM D')} – ${customRange.to?.format(
              'MMM D'
          )}`
        : `${rangeDays}d`

    // Shared tab strip for the Top Performers card. Boats + Service are
    // available to all tiers; Dealers leads for super-admin/manufacturer and is
    // pushed to the right for a dealer.
    const perfTabsNode = (
        <Tabs
            value={perfTab}
            onChange={(_e, v) => setPerfTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
                px: 1.5,
                minHeight: 36,
                '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0.5,
                    textTransform: 'none',
                },
            }}
        >
            {!isDealer && <Tab label="Dealers" value="dealers" />}
            <Tab label="Boats" value="boats" />
            <Tab label="Service" value="service" />
            <Tab label="Location" value="location" />
            {isDealer && <Tab label="Dealers" value="dealers" />}
        </Tabs>
    )

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: 'calc(100vh - 64px)',
                bgcolor: 'background.default',
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 2 },
                pb: { xs: 2, md: 3 },
            }}
        >
            <style>{`@keyframes pulse {0%,100%{opacity:1;}50%{opacity:0.35;}}`}</style>

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                color: 'primary.main',
                                letterSpacing: '0.12em',
                            }}
                        >
                            Fleet Intelligence
                        </Typography>
                        <Typography
                            variant="overline"
                            sx={{
                                color: 'text.secondary',
                                letterSpacing: '0.12em',
                            }}
                        >
                            {isDealer
                                ? dealerScopeLabel
                                : activeManufacturer?.manufacturerName || '—'}
                        </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: -0.5 }}>
                        Fleet Overview
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {isDealer && mfrOptions.length > 1 && (
                        <TextField
                            select
                            size="small"
                            value={selectedMfr}
                            onChange={(e) => setSelectedMfr(e.target.value)}
                            sx={{ minWidth: 180 }}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value="">All Manufacturers</MenuItem>
                            {mfrOptions.map((m) => (
                                <MenuItem key={m._id} value={m._id}>
                                    {m.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <UniversalTimeRange
                        presets={RANGE_OPTIONS}
                        rangeDays={rangeDays}
                        onRangeDays={setRangeDays}
                        customRange={customRange}
                        onCustomRange={setCustomRange}
                    />
                    {/* Timezone the event timestamps render in. Icon-based to
                        match the boat modal; each option carries its own
                        hover tooltip explaining what it means. */}
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={tzMode}
                        onChange={(_e, v) => v && setTzMode(v)}
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: 'text.secondary',
                                borderColor: 'divider',
                                px: 0.9,
                                py: 0.35,
                                '&.Mui-selected': {
                                    color: '#221F1F',
                                    bgcolor: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.main' },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="browser">
                            <Tooltip
                                arrow
                                title={`Browser time — your local timezone (${localTzLabel})`}
                            >
                                <Computer sx={{ fontSize: 16 }} />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="boat">
                            <Tooltip
                                arrow
                                title="Boat local time — each boat's own timezone, from its GPS"
                            >
                                <DirectionsBoat sx={{ fontSize: 16 }} />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="utc">
                            <Tooltip arrow title="UTC — coordinated universal time">
                                <Public sx={{ fontSize: 16 }} />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Circle
                            sx={{
                                fontSize: 10,
                                color: '#8BC441',
                                animation: 'pulse 2s infinite',
                            }}
                        />
                        <AccessTime
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <FleetClock
                            highlightId={
                                tzMode === 'utc'
                                    ? 'utc'
                                    : tzMode === 'browser'
                                    ? localTzLabel === 'UTC'
                                        ? 'utc'
                                        : localTzLabel === 'ET'
                                        ? 'et'
                                        : 'local'
                                    : null
                            }
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* KPI cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                {!kpis
                    ? KPI_DEFS.map((d) => (
                          <Skeleton
                              key={d.key}
                              variant="rounded"
                              height={108}
                              sx={{ flex: '1 1 200px', minWidth: 180 }}
                          />
                      ))
                    : KPI_DEFS.map((d) => {
                          const m = kpis[d.key]
                          return (
                              <KpiStatCard
                                  key={d.key}
                                  label={d.label}
                                  value={d.fmt(m.value)}
                                  pct={m.deltaPct}
                                  invert={m.invert}
                                  series={m.series}
                                  color={d.color}
                                  info={d.description}
                                  fmt={d.fmt}
                                  days={kpis.days}
                              />
                          )
                      })}
            </Box>

            {/* Map + Status */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={6} ref={mapRef}>
                    <Paper elevation={0} sx={{ overflow: 'hidden' }}>
                        <PanelHeader
                            title="Fleet Map"
                            caption="Zoom in to split clusters · hover a boat for detail · toggle statuses below"
                        />
                        {/* Interactive status bar — doubles as a map legend. */}
                        {telemetryReady && byStatus.length > 0 && (
                            <Box sx={{ px: 2.5, pb: 1.5 }}>
                                <FleetStatusBar
                                    data={byStatus}
                                    hidden={hiddenStatuses}
                                    onToggle={toggleStatus}
                                />
                            </Box>
                        )}
                        <Box sx={{ height: 480 }}>
                            {loading || !telemetryReady ? (
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height="100%"
                                />
                            ) : (
                                <UniversalGoogleMapV2
                                    fleetGps={fleetGps}
                                    fetchBoatDetail={fetchBoatDetail}
                                    focusPoint={focusPoint}
                                    resetView={resetView}
                                    style={{
                                        width: '100%',
                                        height: '480px',
                                        backgroundColor: 'inherit',
                                    }}
                                    defaultCenter={{ lat: 20, lng: -25 }}
                                    mapZoom={2}
                                />
                            )}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <TopPerformersCard
                        title="Top Performers"
                        info="by total engine hours · Δ vs prior period"
                        tabs={perfTabsNode}
                        search={boatSearch}
                        onSearch={setBoatSearch}
                        searchPlaceholder={
                            perfTab === 'boats'
                                ? 'Search all boats by HIN or model…'
                                : perfTab === 'dealers'
                                ? 'Filter dealers & boats…'
                                : perfTab === 'location'
                                ? 'Filter locations…'
                                : 'Filter boats…'
                        }
                        searching={searching && perfTab === 'boats'}
                        count={count}
                        onCount={setCount}
                    >
                        {loading || !kpis ? (
                            <Skeleton
                                variant="rounded"
                                height={320}
                                sx={{ m: 2.5 }}
                            />
                        ) : perfTab === 'service' ? (
                            <ServiceTable
                                embedded
                                rangeLabel={rangeLabel}
                                rows={serviceRowsView}
                                tzMode={tzMode}
                                days={kpis?.days}
                                onExpand={handleServiceExpand}
                                detailBySerial={detailBySerial}
                                loadingSerial={
                                    serviceLoading ? null : loadingEventSerial
                                }
                                onExpandChange={(serial, lat, lng) => {
                                    if (serial && lat != null && lng != null) {
                                        setFocusPoint({ lat, lng, id: serial })
                                        mapRef.current?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                        })
                                    } else {
                                        setResetView((v) => v + 1)
                                    }
                                }}
                            />
                        ) : perfTab === 'location' ? (
                            <DrillTable
                                embedded
                                data={locationHierarchy || []}
                                kindLabels={{
                                    country: 'Country',
                                    state: 'State',
                                    city: 'City',
                                }}
                                rootLabel="World"
                                rootIcon={
                                    <Public sx={{ fontSize: 13, mr: 0.4 }} />
                                }
                                rangeLabel={rangeLabel}
                                count={count}
                                search={boatSearch}
                            />
                        ) : perfTab === 'dealers' ? (
                            <DrillTable
                                embedded
                                data={dealerHierarchy || []}
                                kindLabels={{
                                    dealer: 'Dealer',
                                    boat: 'Boat',
                                }}
                                rootLabel="All Dealers"
                                rangeLabel={rangeLabel}
                                count={count}
                                search={boatSearch}
                                onBoatClick={handleBoatClick}
                            />
                        ) : (
                            <DealerPerformanceTable
                                embedded
                                mode="boats"
                                hideSparkline={boatsLight}
                                days={searchActive ? searchDays : kpis?.days}
                                onRowClick={handleBoatClick}
                                entityLabel="Boat"
                                data={boatsData}
                                rangeLabel={rangeLabel}
                            />
                        )}
                    </TopPerformersCard>
                </Grid>
            </Grid>
        </Box>
    )
}

export default FleetHomeView
